import React from "react";
import { ChevronRight, Hourglass, Lock } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

// ── Access state machine ────────────────────────────────────────────────────────
export function getTripState(trip, isAdmin = false) {
  if (!trip) return "loading";
  if (isAdmin) return "full";
  if (trip.payment_status === "paid") {
    const expired = trip.access_expired ?? (() => {
      const retour = new Date(trip.date_retour);
      const expiry = new Date(retour.getTime() + 15 * 24 * 60 * 60 * 1000);
      return new Date() > expiry;
    })();
    if (expired) return "expired";
    return "full";
  }
  if (!trip.admin_validated) return "pending";
  return "payment";
}

// ── Pending validation screen ───────────────────────────────────────────────────
export function PendingValidation({ trip }) {
  const { t } = useLanguage();
  return (
    <div className="max-w-lg mx-auto py-16 px-5 text-center">
      <div className="card-surface animate-fade-in-up">
        <div className="w-16 h-16 border-2 border-[#DCD4C6] rounded-full flex items-center justify-center mx-auto mb-6">
          <Hourglass size={28} strokeWidth={1.5} className="text-[#C17F59]" />
        </div>
        <p className="section-label mb-3">{t("trip.pending_label")}</p>
        <h2
          className="text-3xl mb-4"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}
        >
          {t("trip.pending_title")}
        </h2>
        <p className="text-sm text-[#5C554E] leading-relaxed mb-6 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          {t("trip.pending_desc", { dest: trip.destination })}
        </p>
        <div className="bg-[#FDF8F3] border border-[#DCD4C6] p-4 text-left rounded-sm">
          <p className="text-xs uppercase tracking-widest text-[#8B5E34] mb-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>{t("trip.pending_what_label")}</p>
          <ul className="space-y-2">
            {[
              t("trip.pending_feat1"),
              t("trip.pending_feat2"),
              t("trip.pending_feat3"),
              t("trip.pending_feat4"),
            ].map((f, i) => (
              <li key={i} className="text-xs text-[#5C554E] flex items-start gap-2" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                <ChevronRight size={11} strokeWidth={2} className="text-[#C17F59] mt-0.5 flex-shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-[#9A8F86] mt-5 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          {t("trip.pending_note")}
        </p>
      </div>
    </div>
  );
}

// ── Expired access screen ───────────────────────────────────────────────────────
export function ExpiredAccess({ trip }) {
  const { t } = useLanguage();
  return (
    <div className="max-w-lg mx-auto py-16 px-5 text-center">
      <div className="card-surface animate-fade-in-up">
        <div className="w-16 h-16 border-2 border-[#DCD4C6] rounded-full flex items-center justify-center mx-auto mb-6">
          <Lock size={28} strokeWidth={1.5} className="text-[#9A8F86]" />
        </div>
        <h2
          className="text-3xl mb-4"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}
        >
          {t("trip.expired_title")}
        </h2>
        <p className="text-sm text-[#5C554E] leading-relaxed italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          {t("trip.expired_desc", { dest: trip.destination })}
        </p>
      </div>
    </div>
  );
}
